import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import PageHero from '../components/PageHero';
import { API_URL } from '../config';

const Contact = () => {
  console.log('Contact component rendered');
  
  const [contactInfo, setContactInfo] = useState(null);
  const [heroData, setHeroData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([fetchContactInfo(), fetchHeroData()]);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Bilgiler yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const fetchHeroData = async () => {
    try {
      console.log('Fetching hero data...');
      const response = await fetch(`${API_URL}/api/heroes?populate=*`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      console.log('Hero API Response:', result);
      
      if (result.data && result.data[0]) {
        const heroItem = result.data[0];
        setHeroData({
          title: 'İletişim',
          subtitle: 'Hukuki danışmanlık için bizimle iletişime geçin',
          image: heroItem.image?.formats?.large?.url ? 
            `${API_URL}${heroItem.image.formats.large.url}` : null
        });
      }
    } catch (error) {
      console.error('Error fetching hero:', error);
    }
  };

  const fetchContactInfo = async () => {
    try {
      console.log('Fetching contact info...');
      const response = await axios.get(`${API_URL}/api/contact-infos?populate=*`);
      console.log('Contact API Response:', response.data);
      
      if (response.data?.data && response.data.data.length > 0) {
        const data = response.data.data[0];
        console.log('Setting contact info:', data);
        setContactInfo(data);
      }
    } catch (err) {
      console.error('Error fetching contact info:', err);
      throw err;
    }
  };

  const downloadVCard = () => {
    console.log('Attempting to download vCard with data:', contactInfo);
    if (!contactInfo) return;
    
    const vcard = `BEGIN:VCARD
VERSION:3.0
FN:${contactInfo.name || ''}
TITLE:${contactInfo.title || ''}
TEL;TYPE=WORK,VOICE:${contactInfo.phone || ''}
EMAIL;TYPE=WORK:${contactInfo.email || ''}
ADR;TYPE=WORK:;;${contactInfo.address || ''}
END:VCARD`;

    const blob = new Blob([vcard], { type: 'text/vcard' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${(contactInfo.name || 'contact').replace(/\s+/g, '_')}.vcf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    console.log('Showing loading state');
    return <div className="flex justify-center items-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>;
  }

  if (error) {
    console.log('Showing error state:', error);
    return <div className="container mx-auto px-4 py-8">
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    </div>;
  }

  console.log('Rendering main contact content');
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <PageHero 
        title={heroData?.title || 'İletişim'}
        subtitle={heroData?.subtitle || 'Hukuki danışmanlık için bizimle iletişime geçin'}
        image={heroData?.image}
      />

      {/* Contact Content */}
      <div className="bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-semibold mb-6">İletişim Bilgileri</h2>
              
              <div className="space-y-4">
                {/* İsim ve Ünvan */}
                {(contactInfo?.name || contactInfo?.title) && (
                  <div className="flex items-start space-x-3">
                    <svg className="w-6 h-6 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <div>
                      {contactInfo?.name && (
                        <div className="text-gray-900 font-medium">{contactInfo.name}</div>
                      )}
                      {contactInfo?.title && (
                        <div className="text-gray-600">{contactInfo.title}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Adres */}
                {contactInfo?.address && (
                  <div className="flex items-start space-x-3">
                    <svg className="w-6 h-6 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div className="text-gray-600">{contactInfo.address}</div>
                  </div>
                )}

                {/* Telefon */}
                {contactInfo?.phone && (
                  <div className="flex items-start space-x-3">
                    <svg className="w-6 h-6 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <div className="text-gray-600">{contactInfo.phone}</div>
                  </div>
                )}

                {/* Email */}
                {contactInfo?.email && (
                  <div className="flex items-start space-x-3">
                    <svg className="w-6 h-6 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <div className="text-gray-600">{contactInfo.email}</div>
                  </div>
                )}

                {/* Çalışma Saatleri */}
                {contactInfo?.working_hours && (
                  <div className="flex items-start space-x-3">
                    <svg className="w-6 h-6 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-gray-600">
                      <ReactMarkdown>{contactInfo.working_hours}</ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>

              {/* vCard Download Button */}
              <button
                onClick={downloadVCard}
                className="mt-6 inline-flex items-center px-4 py-2 border border-primary text-primary font-medium rounded-md hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Kartviziti İndir
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
